'use client';

import { forwardRef } from 'react';

import { css, cx, splitResponsiveVariant } from '@/styled-system/css';

import { useControllableState } from '../../hooks/use-controllable-state';
import { PaginationRootContext } from './context/pagination-root.context';
import { paginationRootStyles } from './styles/pagination-root.styles';
import type { PaginationRootPropsInterface } from './types/pagination-root.types';

/**
 * Hosts pagination state and the outer `div`
 * @since 10.0.0
 */
export const PaginationRoot = forwardRef<HTMLDivElement, PaginationRootPropsInterface>(
  (props: PaginationRootPropsInterface, forwardedRef) => {
    const {
      'aria-label': ariaLabel = 'Pagination',
      children,
      className,
      'css': cssProp,
      currentPageIndex,
      defaultCurrentPageIndex,
      onPageSelected,
      totalPages,
      showNumberButtons = { base: false, md: true },
      ...rest
    } = props;

    const [page = 0, setPageIndex] = useControllableState({
      prop: currentPageIndex,
      defaultProp: defaultCurrentPageIndex,
      onChange: onPageSelected,
    });

    const styles = css(paginationRootStyles, cssProp);

    const showNumberVariants = splitResponsiveVariant('showNumberButtons', showNumberButtons);

    return (
      <PaginationRootContext.Provider
        value={{
          showNumberButtons: showNumberVariants,
          currentPageIndex: page,
          setPageIndex,
          totalPages,
        }}
      >
        <nav
          aria-label={ariaLabel}
          className={cx('c11n', styles, className)}
          data-c11n-component="Pagination.Root"
          data-c11n-version={__C11N_VERSION__}
          ref={forwardedRef}
          {...rest}
        >
          {children}
        </nav>
      </PaginationRootContext.Provider>
    );
  },
);

PaginationRoot.displayName = 'Pagination.Root';
